/**
 * Accordion — Direction B rule 8: a rail of sections, one open at a time.
 *
 * The editor rail and the Mock-ups run panel stack seven and five control cards, all open,
 * all of the time; the rail is taller than any viewport and the operator scrolls past four
 * cards to reach the one they are using. Direction B collapses them into one accordion.
 *
 * WHY THIS IS A COMPONENT AND NOT A BARE `<details name>`. HTML has an exclusive-accordion
 * attribute and every target browser implements it, so the markup below emits it — a page
 * built on this keeps working with JavaScript doing nothing. But **jsdom does not implement
 * it**, and jsdom is where every page wave's tests run: a rail built on the bare attribute
 * would have its central behaviour untestable in the suite that guards it. So the open key is
 * owned here, in React, and the attribute is emitted as well. Belt and braces, and the braces
 * are the ones the test harness can see.
 *
 * Closing the open section leaves NONE open, deliberately: "collapse everything" is a state an
 * operator reaches for when they want the canvas, and forcing one section open forever takes
 * it away.
 *
 * ## Controlled and uncontrolled (fix round 1, F-6)
 *
 * Pass `openKey` + `onOpenChange` and the parent owns the open section — which is what Z6's
 * mask rail needs (selecting a region opens the card that edits it) and what Z3's run panel
 * needs. Pass neither and the component owns it, seeded once by `defaultOpen`.
 *
 * **`defaultOpen` is read ONCE, at mount.** Changing it later does nothing, on purpose: that
 * is what "default" means, and a prop that is sometimes a seed and sometimes a command is the
 * bug this note exists to prevent. If you need to open a section in response to a selection,
 * use `openKey`. Remounting with `key=` also works and throws away every card's scroll
 * position and focus, which is why the controlled pair exists.
 *
 * One-open-at-a-time is the primitive's invariant in both forms: a controlled parent that sets
 * `openKey` to a key that is not in `items` simply leaves none open.
 *
 * ## The `meta` slot is not part of the toggle (fix round 1, F-7)
 *
 * `meta` renders inside the `<summary>`, and every click inside a `<summary>` toggles its
 * `<details>`. A `<button>` there used to fire its own handler AND collapse the section under
 * the pointer. The summary's click handler now stops at the slot: a control inside `meta`
 * does its own job and nothing else, by pointer or by keyboard (the inner control's handler
 * runs first, on the way up, before this one decides not to toggle).
 */
import { Fragment, useId, useState, type ReactNode } from "react";

export interface AccordionItem {
  key: string;
  /** The `<summary>` label. */
  head: ReactNode;
  body: ReactNode;
  /**
   * Optional trailing element in the header row — a count, a badge, a state dot, or a real
   * control. Clicks inside it do NOT toggle the section.
   */
  meta?: ReactNode;
  /**
   * WS-AUTOKEY — an optional CAPTION for a run of sections. It is drawn once, before the first
   * item of the run, in the inspector head's voice (12 / 500, muted), and it is not a toggle.
   * The wizard's admin rail uses it to put every tool a customer never sees under "Advanced"
   * while the rail stays ONE accordion with one section open at a time.
   */
  group?: string;
}

export interface AccordionProps {
  /**
   * The accordion's name, emitted as `data-accordion`. The `<details name>` group is this
   * name plus the instance's id: every page stays mounted, so two instances of one accordion
   * can be in the document together, and one shared group made each close the other's open
   * section and re-open its own, without end.
   */
  name: string;
  items: AccordionItem[];
  /**
   * UNCONTROLLED: the key open on first render, read **once**. Later changes are ignored —
   * use `openKey` if the parent needs to drive it.
   */
  defaultOpen?: string;
  /**
   * CONTROLLED: the open key, or `null` for none. Supplying it (even as `null`) hands the
   * open section to the parent; `onOpenChange` is then the only way it moves.
   */
  openKey?: string | null;
  /** Fires with the key that is now open, or `null` when the open one was closed. */
  onOpenChange?: (key: string | null) => void;
}

export function Accordion({ name, items, defaultOpen, openKey, onOpenChange }: AccordionProps) {
  const [ownKey, setOwnKey] = useState<string | null>(defaultOpen ?? null);
  const group = `${name}-${useId()}`;
  const controlled = openKey !== undefined;
  const open = controlled ? openKey : ownKey;
  const toggle = (key: string) => {
    const next = open === key ? null : key;
    if (!controlled) setOwnKey(next);
    onOpenChange?.(next);
  };
  return (
    <div className="accordion">
      {items.map((it, i) => (
        <Fragment key={it.key}>
        {it.group && it.group !== items[i - 1]?.group && (
          <div className="accordion__group">{it.group}</div>
        )}
        <details
          className="accordion__item"
          name={group}
          data-accordion={name}
          open={open === it.key}
          /* The `toggle` event is NOT cancelable, so this cannot veto anything — what keeps
             React authoritative is the `preventDefault()` on the summary's click, below. What
             this DOES do is re-impose React's state when something else moved the element:
             find-in-page auto-expanding a closed section, or `<details name>`'s own exclusivity
             closing a sibling. Without it the DOM and the render disagree until the next
             unrelated re-render. */
          onToggle={(e) => {
            const want = open === it.key;
            if (e.currentTarget.open !== want) e.currentTarget.open = want;
          }}
        >
          <summary
            className="accordion__head"
            onClick={(e) => {
              // Always: the browser must not own `open` (see `onToggle`).
              e.preventDefault();
              // F-7: the meta slot is a place for a control, not a second toggle target.
              const from = e.target instanceof Element ? e.target.closest(".accordion__meta") : null;
              if (from && e.currentTarget.contains(from)) return;
              toggle(it.key);
            }}
          >
            <span className="accordion__head-label">{it.head}</span>
            {it.meta === undefined ? null : <span className="accordion__meta">{it.meta}</span>}
          </summary>
          <div className="accordion__body">{it.body}</div>
        </details>
        </Fragment>
      ))}
    </div>
  );
}
